import { useEffect, useRef, useState } from 'react';
import 'aframe';
import Hls from 'hls.js';
import LoadingScreen from '../LoadingScreen';

// Sphere rotation controls - rotate the sphere instead of the camera
// This avoids gimbal lock / pole issues with traditional look-controls
function registerSphereControls() {
  const AFRAME = (window as any).AFRAME;
  const THREE = (window as any).THREE;
  if (!AFRAME || AFRAME.components['sphere-controls']) return;

  AFRAME.registerComponent('sphere-controls', {
    schema: {
      sensitivity: { type: 'number', default: 0.004 },
      mode: { type: 'string', default: 'touch' } // 'touch' or 'gyro'
    },

    init: function() {
      const self = this;

      // Quaternions for sphere rotation
      self.manualQuat = new THREE.Quaternion();
      self.deviceQuat = new THREE.Quaternion();
      self.tempQuat = new THREE.Quaternion();
      self.gyroEnabled = false;
      self.screenOrientation = 0;

      // Touch/mouse state
      self.isDragging = false;
      self.lastX = 0;
      self.lastY = 0;

      // Get all sphere elements (grid and video)
      self.spheres = [];
      self.videosphere = null;

      self.findSpheres = () => {
        self.spheres = Array.from(document.querySelectorAll('.controllable-sphere'));
        self.videosphere = document.querySelector('a-videosphere');
        if (self.videosphere && !self.spheres.includes(self.videosphere)) {
          self.spheres.push(self.videosphere);
        }
      };

      // Offset quaternion for touch adjustments in gyro mode
      self.gyroOffset = new THREE.Quaternion();

      // Apply rotation to all spheres based on mode
      self.updateSphere = () => {
        self.findSpheres();
        const quat = self.data.mode === 'gyro' && self.gyroEnabled
          ? self.deviceQuat.clone().invert().multiply(self.gyroOffset)
          : self.manualQuat;

        self.spheres.forEach((sphere: any) => {
          if (sphere?.object3D) {
            sphere.object3D.quaternion.copy(quat);
          }
        });

        // Also update THREE.js grid mesh if it exists
        const gridMesh = (window as any).__gridMesh;
        if (gridMesh) {
          gridMesh.quaternion.copy(quat);
        }
      };

      const onStart = (x: number, y: number) => {
        // Allow touch in both modes
        self.isDragging = true;
        self.lastX = x;
        self.lastY = y;
      };

      const onMove = (x: number, y: number) => {
        if (!self.isDragging) return;

        const deltaX = x - self.lastX;
        const deltaY = y - self.lastY;

        if (self.data.mode === 'gyro') {
          // Gyro mode: horizontal drag adjusts yaw, vertical drag adjusts pitch (Z axis in gyro space)
          self.tempQuat.setFromAxisAngle(new THREE.Vector3(0, 1, 0), -deltaX * self.data.sensitivity);
          self.gyroOffset.premultiply(self.tempQuat);

          // Use Z axis for pitch in gyro mode (axes are swapped due to device orientation)
          self.tempQuat.setFromAxisAngle(new THREE.Vector3(0, 0, 1), deltaY * self.data.sensitivity);
          self.gyroOffset.premultiply(self.tempQuat);

          self.updateSphere();
        } else {
          // Touch mode: rotate sphere for both axes
          self.tempQuat.setFromAxisAngle(new THREE.Vector3(0, 1, 0), -deltaX * self.data.sensitivity);
          self.manualQuat.premultiply(self.tempQuat);

          self.tempQuat.setFromAxisAngle(new THREE.Vector3(1, 0, 0), -deltaY * self.data.sensitivity);
          self.manualQuat.premultiply(self.tempQuat);

          self.updateSphere();
        }

        self.lastX = x;
        self.lastY = y;
      };

      const onEnd = () => {
        self.isDragging = false;
      };

      // Device orientation handler
      self.onDeviceOrientation = (e: DeviceOrientationEvent) => {
        if (e.alpha === null) return;
        self.gyroEnabled = true;

        const alpha = THREE.MathUtils.degToRad(e.alpha || 0);
        const beta = THREE.MathUtils.degToRad(e.beta || 0);
        const gamma = THREE.MathUtils.degToRad(e.gamma || 0);
        const orient = THREE.MathUtils.degToRad(self.screenOrientation);

        // Convert device orientation to quaternion
        const euler = new THREE.Euler(beta, alpha, -gamma, 'YXZ');
        self.deviceQuat.setFromEuler(euler);

        // Apply device-to-world transform
        const q1 = new THREE.Quaternion(-Math.sqrt(0.5), 0, 0, Math.sqrt(0.5));
        self.deviceQuat.multiply(q1);

        // Apply screen orientation
        const q2 = new THREE.Quaternion();
        q2.setFromAxisAngle(new THREE.Vector3(0, 0, 1), -orient);
        self.deviceQuat.multiply(q2);

        if (self.data.mode === 'gyro') {
          self.updateSphere();
        }
      };

      self.onOrientationChange = () => {
        self.screenOrientation = (window as any).orientation || 0;
      };

      // Mouse handlers
      self.onMouseDown = (e: MouseEvent) => onStart(e.clientX, e.clientY);
      self.onMouseMove = (e: MouseEvent) => onMove(e.clientX, e.clientY);
      self.onMouseUp = () => onEnd();

      // Touch handlers
      self.onTouchStart = (e: TouchEvent) => {
        if (e.touches.length === 1) {
          onStart(e.touches[0].clientX, e.touches[0].clientY);
        }
      };
      self.onTouchMove = (e: TouchEvent) => {
        if (e.touches.length === 1) {
          e.preventDefault();
          onMove(e.touches[0].clientX, e.touches[0].clientY);
        }
      };
      self.onTouchEnd = () => onEnd();

      this.el.sceneEl.addEventListener('loaded', () => {
        self.videosphere = document.querySelector('a-videosphere');

        const canvas = self.el.sceneEl.canvas;
        if (canvas) {
          canvas.style.cursor = 'grab';
          canvas.addEventListener('mousedown', self.onMouseDown);
          canvas.addEventListener('mousemove', self.onMouseMove);
          canvas.addEventListener('mouseup', self.onMouseUp);
          canvas.addEventListener('mouseleave', self.onMouseUp);
          canvas.addEventListener('touchstart', self.onTouchStart, { passive: true });
          canvas.addEventListener('touchmove', self.onTouchMove, { passive: false });
          canvas.addEventListener('touchend', self.onTouchEnd);
        }

        // Gyro support - always listen, mode determines if we use it
        window.addEventListener('orientationchange', self.onOrientationChange);

        if (typeof (DeviceOrientationEvent as any).requestPermission === 'function') {
          document.addEventListener('click', function requestGyro() {
            (DeviceOrientationEvent as any).requestPermission()
              .then((response: string) => {
                if (response === 'granted') {
                  window.addEventListener('deviceorientation', self.onDeviceOrientation);
                }
              })
              .catch(console.error);
            document.removeEventListener('click', requestGyro);
          }, { once: true });
        } else {
          window.addEventListener('deviceorientation', self.onDeviceOrientation);
        }
      });
    },

    update: function() {
      // When mode changes, update the sphere
      this.updateSphere();
    },

    remove: function() {
      const canvas = this.el.sceneEl?.canvas;
      if (canvas) {
        canvas.removeEventListener('mousedown', this.onMouseDown);
        canvas.removeEventListener('mousemove', this.onMouseMove);
        canvas.removeEventListener('mouseup', this.onMouseUp);
        canvas.removeEventListener('mouseleave', this.onMouseUp);
        canvas.removeEventListener('touchstart', this.onTouchStart);
        canvas.removeEventListener('touchmove', this.onTouchMove);
        canvas.removeEventListener('touchend', this.onTouchEnd);
      }
      window.removeEventListener('deviceorientation', this.onDeviceOrientation);
      window.removeEventListener('orientationchange', this.onOrientationChange);
    }
  });
}

interface AFramePlayerProps {
  videoUrl: string;
  startTime?: number;
  autoPlay?: boolean;
}

declare global {
  namespace JSX {
    interface IntrinsicElements {
      'a-scene': any;
      'a-assets': any;
      'a-videosphere': any;
      'a-camera': any;
      'a-entity': any;
    }
  }
}

export default function AFramePlayer({ videoUrl, startTime = 0, autoPlay = true }: AFramePlayerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const sceneRef = useRef<HTMLElement | null>(null);
  const hlsRef = useRef<Hls | null>(null);
  const videosphereRef = useRef<HTMLElement | null>(null);
  const cameraRef = useRef<HTMLElement | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [sceneLoaded, setSceneLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showHint, setShowHint] = useState(true);
  const [currentQuality, setCurrentQuality] = useState<string>('');
  const [controlMode, setControlMode] = useState<'touch' | 'gyro'>('touch');

  // Update sphere-controls mode when controlMode changes
  useEffect(() => {
    if (!cameraRef.current) return;
    cameraRef.current.setAttribute('sphere-controls', `sensitivity: 0.004; mode: ${controlMode}`);

    // Toggle VR button visibility based on mode
    if (sceneRef.current) {
      sceneRef.current.setAttribute('vr-mode-ui', `enabled: ${controlMode === 'gyro'}`);
    }
  }, [controlMode]);

  useEffect(() => {
    if (!containerRef.current) return;

    // Register custom sphere controls
    registerSphereControls();

    // Create the scene dynamically
    const scene = document.createElement('a-scene');
    scene.setAttribute('embedded', ''); // Must be empty string, not 'true'!
    scene.setAttribute('loading-screen', 'enabled: false');
    scene.setAttribute('vr-mode-ui', 'enabled: false');
    scene.setAttribute('device-orientation-permission-ui', 'enabled: true');
    scene.setAttribute('background', 'color: #1a1a2e');
    // CRITICAL: Must set dimensions for embedded mode!
    scene.style.cssText = 'display: block; width: 100%; height: 100%; position: absolute; top: 0; left: 0; z-index: 1;';
    sceneRef.current = scene;

    // Create assets container - longer timeout for HLS
    const assets = document.createElement('a-assets');
    assets.setAttribute('timeout', '60000');

    // Create video element
    const video = document.createElement('video');
    video.id = 'video-360';
    video.crossOrigin = 'anonymous';
    video.playsInline = true;
    video.loop = true;
    video.muted = true;
    video.preload = 'auto';
    videoRef.current = video;

    // Check if HLS stream
    const isHLS = videoUrl.includes('.m3u8');
    let hls: Hls | null = null;

    // Track if video is already ready (for fast-loading scenarios on PC)
    let videoAlreadyReady = false;

    // Always use hls.js for HLS when supported (better control than native)
    // hls.js works on Safari too, native is just a fallback
    if (isHLS && Hls.isSupported()) {
      console.log('Using HLS.js for adaptive streaming');
      hls = new Hls({
        enableWorker: true,
        lowLatencyMode: false,
        startLevel: 0, // Start at lowest quality (480p) for fast initial load
        abrEwmaDefaultEstimate: 5000000, // 5Mbps - assume decent connection for ABR
        // Buffer settings - larger buffer for smoother playback
        maxBufferLength: 30,
        maxMaxBufferLength: 60,
        backBufferLength: 30,
        maxBufferSize: 100 * 1000 * 1000, // 100MB
      });
      hlsRef.current = hls;

      hls.loadSource(videoUrl);
      hls.attachMedia(video);

      hls.on(Hls.Events.MANIFEST_PARSED, (_event, data) => {
        console.log('HLS manifest loaded, quality levels:', data.levels.length);
        // Show initial quality level
        if (data.levels.length > 0) {
          const initialLevel = hls!.startLevel >= 0 ? hls!.startLevel : 0;
          const level = data.levels[initialLevel];
          if (level) {
            setCurrentQuality(`${level.height}p`);
          }
        }
        // Do NOT call play() here - video frames take minutes to decode
        // Wait until we have enough buffered data before playing
        // The canplaythrough handler will start playback when ready
      });

      // Wait for video to ACTUALLY be playing before switching from logo sphere
      let hasSetReady = false;
      let sphereShownAt = 0; // Track when sphere was first shown
      let playbackStarted = false;

      // Start playback only when we have enough buffered data
      const tryStartPlayback = () => {
        if (playbackStarted) return;
        // Wait for readyState >= HAVE_FUTURE_DATA (3) before playing
        // This means we have enough data to start without stalling
        if (video.readyState >= 3 && video.videoWidth > 0) {
          console.log('Video ready to play, readyState:', video.readyState);
          playbackStarted = true;
          if (autoPlay) {
            video.play().catch(() => console.log('Autoplay blocked'));
          }
        }
      };

      const showVideo = () => {
        if (hasSetReady) return;
        // Only switch when video is actually playing with real frames
        if (!video.paused && video.currentTime > 0 && video.videoWidth > 0) {
          // Ensure logo sphere is shown for at least 1 second
          const now = Date.now();
          if (sphereShownAt === 0) {
            sphereShownAt = now;
          }
          const elapsed = now - sphereShownAt;
          if (elapsed < 1000) {
            console.log('Waiting for minimum sphere display time, elapsed:', elapsed);
            setTimeout(showVideo, 1000 - elapsed);
            return;
          }

          console.log('Video actually playing at:', video.currentTime, 'dimensions:', video.videoWidth, 'x', video.videoHeight);
          hasSetReady = true;
          videoAlreadyReady = true;

          // Hide grid mesh, show videosphere
          const mesh = (window as any).__gridMesh;
          const vs = document.querySelector('a-videosphere');
          console.log('Switching to video sphere');
          if (mesh) mesh.visible = false;
          if (vs) vs.setAttribute('visible', 'true');

          // Now unmute since video is visible and playing
          video.muted = false;
          console.log('Video unmuted - sphere is now visible');

          setIsReady(true);
        }
      };

      // Listen for canplay/canplaythrough to start playback when ready
      video.addEventListener('canplay', tryStartPlayback);
      video.addEventListener('canplaythrough', tryStartPlayback);

      // Check on timeupdate - fires when video is actually progressing
      video.addEventListener('timeupdate', showVideo);
      video.addEventListener('playing', showVideo);

      hls.on(Hls.Events.LEVEL_SWITCHED, (_event, data) => {
        const level = hls!.levels[data.level];
        if (level) {
          const qualityName = `${level.height}p`;
          setCurrentQuality(qualityName);
          console.log('Quality switched to:', qualityName);
        }
      });

      hls.on(Hls.Events.ERROR, (_event, data) => {
        console.error('HLS error:', data);
        if (data.fatal) {
          setError('Failed to load video stream');
        }
      });
    } else if (isHLS && video.canPlayType('application/vnd.apple.mpegurl')) {
      // Native HLS support (Safari/iOS)
      console.log('Using native HLS support');
      video.src = videoUrl;

      // Monitor quality changes via video dimensions
      let lastHeight = 0;
      const checkQuality = () => {
        if (video.videoHeight && video.videoHeight !== lastHeight) {
          lastHeight = video.videoHeight;
          setCurrentQuality(`${video.videoHeight}p`);
          console.log('Native HLS quality:', video.videoHeight);
        }
      };
      video.addEventListener('loadeddata', checkQuality);
      video.addEventListener('resize', checkQuality);
      // Poll for quality changes since resize event isn't always reliable
      const qualityInterval = setInterval(checkQuality, 2000);
      video.addEventListener('ended', () => clearInterval(qualityInterval));
    } else {
      // Regular MP4
      console.log('Using direct video source');
      video.src = videoUrl;
    }

    video.onloadedmetadata = () => {
      console.log('Video metadata loaded, duration:', video.duration);
      if (startTime > 0 && startTime < video.duration) {
        video.currentTime = startTime;
      }
    };

    // For non-HLS or native HLS (Safari), wait for canplaythrough
    let nativeReady = false;
    const showNativeVideo = () => {
      if (nativeReady || (isHLS && Hls.isSupported())) return;
      if (video.readyState >= 4 && video.videoWidth > 0) {
        console.log('Native video ready:', video.videoWidth, 'x', video.videoHeight);
        nativeReady = true;
        videoAlreadyReady = true; // Flag for scene loaded handler
        // Hide grid mesh, show videosphere
        const mesh = (window as any).__gridMesh;
        if (mesh) mesh.visible = false;
        const vs = document.querySelector('a-videosphere');
        if (vs) vs.setAttribute('visible', 'true');
        setIsReady(true);
        if (autoPlay) {
          video.play().catch(() => console.log('Autoplay blocked'));
        }
      }
    };
    video.addEventListener('canplaythrough', showNativeVideo);
    video.addEventListener('playing', showNativeVideo);

    video.onerror = () => {
      console.error('Video error:', video.error);
      setError('Failed to load video');
    };

    assets.appendChild(video);
    scene.appendChild(assets);

    // Store reference to grid mesh for later visibility control
    let gridMesh: any = null;

    // Create logo texture for the loading sphere
    const createLogoTexture = (THREE: any): any => {
      const canvas = document.createElement('canvas');
      const size = 512;
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d')!;

      // Dark transparent background
      ctx.fillStyle = 'rgba(26, 26, 46, 0.3)';
      ctx.fillRect(0, 0, size, size);

      // Draw logo pattern - 4x4 grid
      const logoWidth = size / 4;
      const logoHeight = logoWidth * 0.45;
      const paddingY = (size / 4 - logoHeight) / 2;

      ctx.fillStyle = 'rgba(255, 255, 255, 0.6)';
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.6)';
      ctx.lineWidth = 2;

      for (let row = 0; row < 4; row++) {
        for (let col = 0; col < 4; col++) {
          const x = col * (size / 4);
          const y = row * (size / 4) + paddingY;
          const offsetX = row % 2 === 1 ? size / 8 : 0;
          const pillX = x + offsetX + 4;
          const pillY = y + 4;
          const pillW = logoWidth - 8;
          const pillH = logoHeight - 8;
          const radius = pillH / 2;

          // Draw pill border
          ctx.beginPath();
          ctx.moveTo(pillX + radius, pillY);
          ctx.lineTo(pillX + pillW - radius, pillY);
          ctx.arc(pillX + pillW - radius, pillY + radius, radius, -Math.PI / 2, Math.PI / 2);
          ctx.lineTo(pillX + radius, pillY + pillH);
          ctx.arc(pillX + radius, pillY + radius, radius, Math.PI / 2, -Math.PI / 2);
          ctx.closePath();
          ctx.stroke();

          // Draw "360" text
          ctx.font = `bold ${logoHeight * 0.45}px Arial`;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText('360', pillX + pillW / 2, pillY + pillH * 0.6);
        }
      }

      const texture = new THREE.CanvasTexture(canvas);
      texture.wrapS = THREE.RepeatWrapping;
      texture.wrapT = THREE.RepeatWrapping;
      texture.repeat.set(2, 1);
      return texture;
    };

    // Create logo sphere as soon as THREE.js scene is available (poll for it)
    // Don't wait for 'loaded' event since that waits for video asset too
    // IMPORTANT: Always create the sphere, don't check videoAlreadyReady here!
    let logoSphereCreated = false;
    const createLogoSphere = () => {
      if (logoSphereCreated) return; // Only check if already created, NOT video state

      const THREE = (window as any).THREE;
      const sceneObj = (scene as any).object3D;

      if (THREE && sceneObj) {
        logoSphereCreated = true;
        console.log('Creating logo sphere with texture');

        // Create textured sphere with logo pattern
        const geometry = new THREE.SphereGeometry(500, 64, 32);
        const texture = createLogoTexture(THREE);
        const material = new THREE.MeshBasicMaterial({
          map: texture,
          side: THREE.BackSide,
          transparent: true,
          opacity: 0.9
        });
        gridMesh = new THREE.Mesh(geometry, material);
        gridMesh.name = 'logo-sphere';
        sceneObj.add(gridMesh);
        console.log('Logo sphere added to scene');

        // Store reference for sphere-controls to find
        (window as any).__gridMesh = gridMesh;

        // If video is already ready, hide the sphere immediately
        if (videoAlreadyReady) {
          console.log('Video already ready, hiding logo sphere');
          gridMesh.visible = false;
        }

        setSceneLoaded(true);
      }
    };

    // Poll for THREE.js availability (fires much earlier than 'loaded')
    const pollInterval = setInterval(() => {
      createLogoSphere();
      if (logoSphereCreated) {
        clearInterval(pollInterval);
      }
    }, 50);

    // Also try on renderstart (earlier than loaded)
    scene.addEventListener('renderstart', createLogoSphere);

    // Fallback: also listen for loaded
    scene.addEventListener('loaded', () => {
      clearInterval(pollInterval);
      if (!logoSphereCreated) {
        createLogoSphere();
      }
      setSceneLoaded(true);
    });

    // Create videosphere - hidden initially, rotation handled by sphere-controls
    const videosphere = document.createElement('a-videosphere');
    videosphere.setAttribute('src', '#video-360');
    videosphere.setAttribute('class', 'controllable-sphere');
    videosphere.setAttribute('visible', 'false');
    videosphereRef.current = videosphere;
    scene.appendChild(videosphere);

    // Camera - no look-controls, we rotate the sphere instead
    const camera = document.createElement('a-camera');
    camera.setAttribute('look-controls', 'enabled: false');
    camera.setAttribute('wasd-controls', 'enabled: false');
    camera.setAttribute('fov', '80');
    camera.setAttribute('sphere-controls', 'sensitivity: 0.004');
    cameraRef.current = camera;
    scene.appendChild(camera);

    // Pinch to zoom
    let initialDistance = 0;
    let initialFov = 80;

    const getTouchDistance = (touches: TouchList) => {
      const dx = touches[0].clientX - touches[1].clientX;
      const dy = touches[0].clientY - touches[1].clientY;
      return Math.sqrt(dx * dx + dy * dy);
    };

    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length === 2) {
        initialDistance = getTouchDistance(e.touches);
        const cam = (cameraRef.current as any)?.components?.camera?.camera;
        if (cam) initialFov = cam.fov;
      }
    };

    const onTouchMove = (e: TouchEvent) => {
      if (e.touches.length === 2) {
        e.preventDefault();
        const currentDistance = getTouchDistance(e.touches);
        const scale = initialDistance / currentDistance;
        const newFov = Math.max(20, Math.min(120, initialFov * scale));
        const cam = (cameraRef.current as any)?.components?.camera?.camera;
        if (cam) {
          cam.fov = newFov;
          cam.updateProjectionMatrix();
        }
      }
    };

    scene.addEventListener('loaded', () => {
      const canvas = (scene as any).canvas;
      if (canvas) {
        canvas.addEventListener('touchstart', onTouchStart, { passive: true });
        canvas.addEventListener('touchmove', onTouchMove, { passive: false });
      }
    });

    // Add scene to container
    containerRef.current.appendChild(scene);

    // Hide hint after a few seconds
    const hintTimer = setTimeout(() => setShowHint(false), 5000);

    return () => {
      clearTimeout(hintTimer);
      clearInterval(pollInterval);
      // Cleanup HLS
      if (hls) {
        hls.destroy();
      }
      video.pause();
      video.src = '';
      // Clean up grid mesh reference
      delete (window as any).__gridMesh;
      if (containerRef.current && containerRef.current.contains(scene)) {
        containerRef.current.removeChild(scene);
      }
    };
  }, [videoUrl, startTime, autoPlay]);

  // Adjust sphere via the sphere-controls quaternion system
  const adjustSphereRotation = (axis: 'x' | 'y' | 'z', delta: number) => {
    if (!cameraRef.current) return;
    const THREE = (window as any).THREE;
    const sphereControls = (cameraRef.current as any).components?.['sphere-controls'];
    if (!sphereControls) return;

    // Get the target quaternion based on current mode
    const targetQuat = controlMode === 'gyro' ? sphereControls.gyroOffset : sphereControls.manualQuat;
    if (!targetQuat) return;

    // In gyro mode, swap X and Z axes (pitch <-> roll)
    let actualAxis = axis;
    if (controlMode === 'gyro') {
      if (axis === 'x') actualAxis = 'z';
      else if (axis === 'z') actualAxis = 'x';
    }

    const axisVector = new THREE.Vector3(
      actualAxis === 'x' ? 1 : 0,
      actualAxis === 'y' ? 1 : 0,
      actualAxis === 'z' ? 1 : 0
    );
    const rotQuat = new THREE.Quaternion();
    rotQuat.setFromAxisAngle(axisVector, THREE.MathUtils.degToRad(delta));
    targetQuat.premultiply(rotQuat);

    // Trigger sphere update
    sphereControls.updateSphere();
  };

  const adjustRoll = (delta: number) => adjustSphereRotation('z', delta);

  const handleInteraction = () => {
    const video = videoRef.current;
    if (video) {
      video.muted = false;
      video.play().catch(console.error);
    }
    setShowHint(false);
  };

  if (error) {
    return (
      <div className="fixed inset-0 bg-black flex items-center justify-center">
        <div className="text-center text-white">
          <p className="text-xl mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-3 bg-white/20 rounded-lg hover:bg-white/30 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="fixed inset-0 bg-black"
      onClick={handleInteraction}
    >
      {/* Show loading screen until video is ready, fade when scene loads */}
      {!isReady && (
        <div className={`absolute inset-0 z-50 transition-opacity duration-500 pointer-events-none ${sceneLoaded ? 'opacity-0' : ''}`}>
          <LoadingScreen />
        </div>
      )}

      {isReady && showHint && (
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/80 text-sm bg-black/60 px-6 py-3 rounded-full pointer-events-none z-40 backdrop-blur-sm">
          Tap to unmute • Drag to look around • Pinch to zoom
        </div>
      )}

      {currentQuality && (
        <div className="absolute top-4 right-4 text-white/60 text-xs bg-black/40 px-2 py-1 rounded pointer-events-none z-40">
          {currentQuality}
        </div>
      )}

      {/* Roll adjustment control - buttons with touch slider between */}
      <div
        className="absolute right-4 top-1/2 -translate-y-1/2 flex flex-col items-center z-40"
        style={{ touchAction: 'none' }}
      >
        <div className="relative bg-gray-600/60 backdrop-blur-sm rounded-full p-1 flex flex-col items-center">
          <button
            onClick={(e) => { e.stopPropagation(); adjustRoll(15); }}
            className="w-10 h-10 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center text-xl z-10"
            title="Roll left 15°"
          >
            ↺
          </button>

          {/* Touch slider area */}
          <div
            className="w-10 h-24 flex items-center justify-center cursor-ns-resize select-none"
            onTouchStart={(e) => {
              e.stopPropagation();
              const touch = e.touches[0];
              (e.currentTarget as any).lastY = touch.clientY;
            }}
            onTouchMove={(e) => {
              e.stopPropagation();
              e.preventDefault();
              const touch = e.touches[0];
              const lastY = (e.currentTarget as any).lastY || touch.clientY;
              const delta = (lastY - touch.clientY) * 0.3;
              if (Math.abs(delta) > 0.1) {
                adjustRoll(delta);
                (e.currentTarget as any).lastY = touch.clientY;
              }
            }}
            onMouseDown={(e) => {
              e.stopPropagation();
              const target = e.currentTarget as any;
              target.lastY = e.clientY;
              target.isSliding = true;
            }}
            onMouseMove={(e) => {
              e.stopPropagation();
              const target = e.currentTarget as any;
              if (!target.isSliding) return;
              const delta = (target.lastY - e.clientY) * 0.3;
              if (Math.abs(delta) > 0.1) {
                adjustRoll(delta);
                target.lastY = e.clientY;
              }
            }}
            onMouseUp={(e) => {
              e.stopPropagation();
              (e.currentTarget as any).isSliding = false;
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as any).isSliding = false;
            }}
          >
            <span className="text-white/70 text-lg">⇅</span>
          </div>

          <button
            onClick={(e) => { e.stopPropagation(); adjustRoll(-15); }}
            className="w-10 h-10 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center text-xl z-10"
            title="Roll right 15°"
          >
            ↻
          </button>
        </div>
      </div>

      {/* Control mode toggle */}
      <div className="absolute bottom-6 right-6 z-50">
        <button
          onClick={(e) => {
            e.stopPropagation();
            setControlMode(prev => prev === 'touch' ? 'gyro' : 'touch');
          }}
          className="px-4 py-2 bg-black/50 hover:bg-black/70 text-white rounded-lg backdrop-blur-sm flex items-center gap-2 text-sm"
          title={controlMode === 'touch' ? 'Switch to Gyro mode' : 'Switch to Touch mode'}
        >
          {controlMode === 'touch' ? (
            <>
              <span>👆</span>
              <span>Touch</span>
            </>
          ) : (
            <>
              <span>📱</span>
              <span>Gyro</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
}
